// apiService.ts
import { formatApiError } from "src/utils/errorFormatter";

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export interface ApiResponse {
  status: number;
  data: any;
  additionalData?: any;
}

export interface ApiError {
  status: number;
  message: string;
}

export async function makeApiCall(
  url: string,
  method: HttpMethod,
  data?: any,
  options?: any,
  signal?: AbortSignal | null,
): Promise<ApiResponse> {
  const { isPublicRoute } = options || {};

  const tokenKey = `${process.env.REACT_APP_JWT_TOKEN_KEY}`;
  const token = tokenKey ? localStorage.getItem(tokenKey) : null;
  let body;
  let headers: HeadersInit = {};

  if (data instanceof FormData) {
    body = data;
  } else if (data) {
    body = JSON.stringify(data);
    headers["Content-Type"] = "application/json";
  }

  if (!isPublicRoute) {
    headers.Authorization = `Bearer ${token}`;
  }

  const requestOptions: RequestInit = {
    method,
    headers,
    body: ["POST", "PUT", "PATCH"].includes(method) ? body : undefined,
    signal,
  };

  if (
    (method === "GET" || method === "DELETE") &&
    data &&
    typeof data === "object"
  ) {
    const queryString = Object.entries(data)
      .filter(([_, value]) => !!value || typeof value === "boolean")
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    url = `${url}?${queryString}`;
  }

  try {
    const response = await fetch(url, requestOptions);
    const contentType = response.headers.get("content-type");

    let responseData: any;

    if (contentType?.includes("application/json")) {
      responseData = await response.json();
    } else if (contentType?.includes("application/pdf")) {
      responseData = await response.blob();
    } else {
      responseData = await response.text();
    }

    let message;
    if (!response.ok) {
      switch (response.status.toString()) {
        case "400":
          let res =
            typeof responseData === "string"
              ? JSON.parse(responseData)
              : responseData;

          const formattedError = formatApiError(res, 400);
          message = formattedError.message;

          if (formattedError.showSupportMessage) {
            message +=
              ".\n\nIf the issue persists, please contact our support team.";
          }

          console.error("API 400 Error Details:", res);

          return {
            data: message, // Keeps existing "Single Message" logic intact[cite: 4]
            status: response.status,
            // Safely add the raw error object here for high-level use[cite: 4]
            additionalData: {
              ...responseData?.additionalData,
              validationErrors: res.errors, // This contains your $.license.issueDate object[cite: 4]
            },
          };
        case "409":
          var respon =
            typeof responseData === "string"
              ? JSON.parse(responseData)
              : responseData;
          const conflictError = formatApiError(respon, 409);
          message = conflictError.message;
          break;
        case "401":
          message =
            "your session has expired due to 4 hours of inactivity. Please sign in again to continue";
          window.dispatchEvent(new CustomEvent("session-expired"));
          break;
        case "403":
          message = "Forbidden: you do not have access rights to the content";
          break;
        case "404":
          message =
            "Not Found: The server can not find the requested resource.";
          break;
        case "500":
        case "502":
          const serverError = formatApiError(responseData, response.status);
          message = 'Something went wrong."';
          if (serverError.showSupportMessage) {
            message +=
              "\n\nIf the issue persists, please contact our support team.";
          }
          break;
        case "429":
          message =
            "Limit Reached: Please remove existing one before adding a new one.";
          var respon =
            typeof responseData === "string"
              ? JSON.parse(responseData)
              : responseData;
          message = respon.detail ? respon.detail : message;
          break;

        default:
          message =
            responseData.Detail || "An error occurred. Please try again later.";
      }
      return {
        data: message,
        status: response.status,
        additionalData: responseData?.additionalData,
      };
    }
    return { data: responseData, status: response.status };
  } catch (error) {
    console.error("Error during API call:", error);
    throw new Error("Failed to make API call");
  }
}
